import { z } from 'zod';

// ============================================================
// Claim Schemas (Story 2.1, Issue #8, Issue #9, Issue #10, Issue #11, Issue #13)
// ============================================================

export const claimStatusEnum = z.enum([
  'DRAFT',
  'SUBMITTED',
  'ADMIN_APPROVED',
  'RETURNED_TO_ADMIN',
  'FINANCE_APPROVED',
  'APPROVED',
  'REJECTED',
]);

export const claimTypeEnum = z.enum([
  'GENERAL',
  'MILEAGE',
  'PETTY_CASH',
  'PROJECT',
]);

export type ClaimType = z.infer<typeof claimTypeEnum>;

export const createClaimSchema = z.object({
  claimMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'claimMonth must be in YYYY-MM format')
    .refine((val) => {
      const month = Number(val.split('-')[1]);
      return month >= 1 && month <= 12;
    }, 'claimMonth month must be between 01 and 12')
    .transform((val) => {
      const [year, month] = val.split('-').map(Number);
      return new Date(Date.UTC(year!, month! - 1, 1));
    }),
  claimType: claimTypeEnum.default('GENERAL').optional(),
  description: z.string().max(500, 'Description cannot exceed 500 characters').optional(),
});

export type CreateClaimInput = z.input<typeof createClaimSchema>;

export const updateClaimSchema = z.object({
  claimType: claimTypeEnum.optional(),
  description: z.string().max(500, 'Description cannot exceed 500 characters').optional().nullable(),
});

export type UpdateClaimInput = z.infer<typeof updateClaimSchema>;

export const claimResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  claimMonth: z.date().or(z.string()),
  claimType: claimTypeEnum.default('GENERAL'),
  description: z.string().nullable().optional(),
  status: claimStatusEnum,
  totalAmount: z.union([z.number(), z.string()]),
  referenceNumber: z.string().nullable(),
  hardcopyReceived: z.boolean().default(false),
  hardcopyReceivedById: z.string().nullable().optional(),
  hardcopyReceivedAt: z.date().or(z.string()).nullable().optional(),
  createdAt: z.date().or(z.string()),
  updatedAt: z.date().or(z.string()),
});

export type ClaimResponse = z.infer<typeof claimResponseSchema>;

// Story A.4 (Issue #10)
export const claimStatusHistorySchema = z.object({
  id: z.string(),
  claimId: z.string(),
  fromStatus: z.string(),
  toStatus: z.string(),
  userId: z.string(),
  userName: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  createdAt: z.date().or(z.string()),
});

export type ClaimStatusHistoryResponse = z.infer<typeof claimStatusHistorySchema>;
